import { FC, useCallback, useContext, useEffect, useState } from "react";

import { Button } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Subtitle } from "capitalrx-components-2.0/lib/typography";
import { Dropdown, Form, SubmitButton, Textbox, UseFormReturn } from "capitalrx-components-2.0/lib/forms";
import { QuestionnaireListResponse, states } from "capitalrx-types";

import { QuestionAnswers } from "@/api/interfaces/Auth";
import { QuestionnaireFormContent, PACaseCreateForm } from "@/components";
import { ThemeSelectorContext } from "@/context";
import { ANALYTIC_EVENTS, logEvent } from "@/service/analyticsService";
import { getThemeButton, getQuestionnaireResponses, prefillQuestionnaireAnswers, validatePhoneNumber, formatPhoneWithExtension } from "@/utils";


export interface PAQuestionnaireForm {
  readonly [key: string]: string | boolean | null;
}

interface FormProps {
  goToNextStep: Function;
  form: UseFormReturn<PACaseCreateForm>;
  questionnaireForm: UseFormReturn<PAQuestionnaireForm>;
  prefillQuestions?: QuestionAnswers;
  showAlternateFaxNumberField: boolean;
  onBack: Function;
}


export const CaseQuestionnaireForm: FC<FormProps> = ({ goToNextStep, form, questionnaireForm, prefillQuestions, showAlternateFaxNumberField, onBack }) => {
  const { primary_color } = useContext(ThemeSelectorContext);

  const [questionnaires, _] = useState<QuestionnaireListResponse>(form.getValues("questionnaires"));
  const alternateFaxNumber = questionnaireForm.watch("alternate_fax_number");

  useEffect(() => {
    if (alternateFaxNumber && typeof alternateFaxNumber === "string") {
      const formatted = formatPhoneWithExtension(alternateFaxNumber);
      if (formatted !== alternateFaxNumber) {
        questionnaireForm.setValue("alternate_fax_number", formatted);
      }
    }
  }, [alternateFaxNumber, questionnaireForm]);

  const { six_six_twelve_columns } = useResponsive({
    six_six_twelve_columns: {
      desktop: 6,
      tablet: 6,
      mobile: 12,
    },
  });

  const { phone_fax_columns } = useResponsive({
    phone_fax_columns: {
      desktop: 3,
      tablet: 3,
      mobile: 12,
    },
  });

  const { email_columns } = useResponsive({
    email_columns: {
      desktop: 6,
      tablet: 6,
      mobile: 12,
    },
  });

  const { city_state_zip_columns } = useResponsive({
    city_state_zip_columns: {
      desktop: 3,
      tablet: 3,
      mobile: 12,
    },
  });

  useEffect(() => {
    // INJECT RESPONSES FROM PREFILL
    prefillQuestionnaireAnswers(questionnaireForm, questionnaires, prefillQuestions);
  }, []);

  useEffect(() => {
    const parentValues = form.getValues();
    const stateAbbreviation = states[parentValues.provider_state] ?? parentValues.provider_state ?? "";
    questionnaireForm.setValue(
      "provider_first_name",
      parentValues.provider_first_name ?? "",
    );

    questionnaireForm.setValue(
      "provider_last_name",
      parentValues.provider_last_name ?? "",
    );

    questionnaireForm.setValue(
      "provider_phone",
      parentValues.provider_phone ?? "",
    );

    questionnaireForm.setValue(
      "provider_fax",
      parentValues.provider_fax ?? "",
    );

    questionnaireForm.setValue(
      "provider_email",
      parentValues.provider_email ?? "",
    );

    questionnaireForm.setValue(
      "provider_address",
      parentValues.provider_address ?? "",
    );

    questionnaireForm.setValue(
      "provider_address_2",
      parentValues.provider_address_2 ?? "",
    );

    questionnaireForm.setValue(
      "provider_city",
      parentValues.provider_city ?? "",
    );

    questionnaireForm.setValue(
      "provider_state",
      stateAbbreviation,
    );

    questionnaireForm.setValue(
      "provider_zip",
      parentValues.provider_zip ?? "",
    );
  }, [form, questionnaireForm]);

  const formSubmitted = useCallback(async (data: PAQuestionnaireForm) => {
    const responses = getQuestionnaireResponses(data, questionnaires);
    form.setValue("provider_first_name", data.provider_first_name as string);
    form.setValue("provider_last_name", data.provider_last_name as string);
    form.setValue("provider_phone", data.provider_phone as string);
    form.setValue("provider_fax", data.provider_fax as string);
    form.setValue("provider_email", data.provider_email as string);
    form.setValue("provider_address", data.provider_address as string);
    form.setValue("provider_address_2", data.provider_address_2 as string);
    form.setValue("provider_city", data.provider_city as string);
    form.setValue("provider_state", data.provider_state as string);
    form.setValue("provider_zip", data.provider_zip as string);
    form.setValue("questionnaire_responses", responses);
    await goToNextStep();
  }, [questionnaires]);

  const goBack = useCallback(() => {
    logEvent(ANALYTIC_EVENTS.PA_CASE_CREATE.back);
    onBack();
  }, [onBack]);

  return (
    <Form form={questionnaireForm} onSubmit={formSubmitted}>
      <div style={{ marginTop: "32px" }} />
      <Stack spacing="large">
        <Stack>
              <Subtitle>Prescriber Contact</Subtitle>
              <Grid>
                <Grid.Span columns={six_six_twelve_columns}>
                  <Textbox.Field
                    label="First Name"
                    name="provider_first_name"
                    required
                  />
                </Grid.Span>

                <Grid.Span columns={six_six_twelve_columns}>
                  <Textbox.Field
                    label="Last Name"
                    name="provider_last_name"
                    required
                  />
                </Grid.Span>
              </Grid>

              <Grid>
                <Grid.Span columns={phone_fax_columns}>
                  <Textbox.Field
                    label="Phone Number"
                    name="provider_phone"
                    validator={validatePhoneNumber}
                    required
                    instructions="123-456-7890"
                  />
                </Grid.Span>

                <Grid.Span columns={phone_fax_columns}>
                  <Textbox.Field
                    label="Fax Number"
                    name="provider_fax"
                    validator={validatePhoneNumber}
                    required
                    instructions="123-456-7890 x 123"
                  />
                </Grid.Span>

                <Grid.Span columns={email_columns}>
                  <Textbox.Field
                    label="Email Address"
                    name="provider_email"
                    instructions="email@address.com"
                  />
                </Grid.Span>
              </Grid>

              <Grid>
                <Grid.Span columns={six_six_twelve_columns}>
                  <Textbox.Field
                    label="Street Address"
                    name="provider_address"
                    required
                  />
                </Grid.Span>

                <Grid.Span columns={six_six_twelve_columns}>
                  <Textbox.Field
                    label="Address Line 2"
                    name="provider_address_2"
                  />
                </Grid.Span>
              </Grid>

              <Grid>
                <Grid.Span columns={city_state_zip_columns}>
                  <Textbox.Field
                    label="City"
                    name="provider_city"
                    required
                  />
                </Grid.Span>

                <Grid.Span columns={city_state_zip_columns}>
                  <Dropdown.Field
                    label="State"
                    name="provider_state"
                    required
                    searchable
                  >
                    {Object.entries(states).map(([full, abbreviation]) => (
                      <Dropdown.Option key={abbreviation} value={abbreviation}>
                        {full}
                      </Dropdown.Option>
                    ))}
                  </Dropdown.Field>
                </Grid.Span>

                <Grid.Span columns={city_state_zip_columns}>
                  <Textbox.Field
                    label="Zip Code"
                    name="provider_zip"
                    required
                  />
                </Grid.Span>
              </Grid>
          {!questionnaires?.results?.length && (
            <span>No Clinical Criteria Questions necessary</span>
          )}
          <div style={{ marginTop: "32px" }} />
          {questionnaires?.results?.length && (
            <Stack>
              <Subtitle>Clinical Criteria Questions</Subtitle>
              {questionnaires.results?.map(({ questionnaire }) => (
                questionnaire.questions?.length && (
                  <QuestionnaireFormContent
                    key={questionnaire.id}
                    answersOpen={true}
                    questionnaire={questionnaire}
                  />
                ) || null
              ))}
            </Stack>
          )}
        </Stack>
        <Shelf spacing="small">
          {!questionnaireForm.formState.isSubmitting && (
            <Button appearance={getThemeButton("secondary", primary_color)} onClick={goBack}>
              Back
            </Button>
          )}
          <SubmitButton appearance={getThemeButton("primary", primary_color)}>
            Submit
          </SubmitButton>
          <Shelf.Spacer />
        </Shelf>
      </Stack>
    </Form>
  );
};

export default CaseQuestionnaireForm;
